import { ArrowRight } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import productsData from '../data/products.json';

function VideoSection() {
  const { t, i18n } = useTranslation();
  const lang = (i18n?.language || '').toLowerCase();
  const isArabic = lang.startsWith('ar');
  const isPersian = lang.startsWith('fa') || lang.startsWith('ir') || lang.includes('persian');
  const items = (productsData && productsData.products) || [];
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target;
          const hidden = (el.getAttribute('data-hidden') || '').split(' ').filter(Boolean);
          const shown = (el.getAttribute('data-shown') || '').split(' ').filter(Boolean);
          if (entry.isIntersecting) {
            if (shown.length) el.classList.add(...shown);
            if (hidden.length) el.classList.remove(...hidden);
          } else {
            if (hidden.length) el.classList.add(...hidden);
            if (shown.length) el.classList.remove(...shown);
          }
        });
      },
      { threshold: 0.25 }
    );
    const elements = document.querySelectorAll('.gs-reveal');
    elements.forEach((el) => {
      const delay = el.getAttribute('data-delay') || '0';
      el.style.transitionDelay = `${delay}ms`;
      // remember element-specific hidden classes (left/right/y)
      const hidden = ['opacity-0', 'scale-95'];
      if (el.classList.contains('-translate-x-6')) hidden.push('-translate-x-6');
      if (el.classList.contains('translate-x-6')) hidden.push('translate-x-6');
      if (el.classList.contains('translate-y-6')) hidden.push('translate-y-6');
      el.setAttribute('data-hidden', hidden.join(' '));
      el.setAttribute('data-shown', 'opacity-100 translate-x-0 translate-y-0 scale-100');
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);
  return (
    <>
      {items.map((product, idx) => {
        const title = product.slogan_title || product.name || 'Protect all your content';
        const description =
          product.slogan_description ||
          product.description ||
          'Dash makes it easy to find files across apps and safeguard sensitive content. With AI-powered search and universal access controls, your team stays connected, efficient, and secure.';
        const rightImage = product.slogan_image || '/assets/images/Slogan/Meeting_1.jpg';
        // Zebra glow variants: keep blue for first product, rotate others
        const ringOffset = 'ring-offset-0 dark:ring-offset-2 dark:ring-offset-slate-900';
        const glowVariants = [
          {
            ring: 'ring-0 dark:ring-2 dark:ring-blue-400/60',
            shadow:
              'shadow-none dark:shadow-[0_0_35px_rgba(96,165,250,0.35)]',
          },
          {
            ring: 'ring-0 dark:ring-2 dark:ring-emerald-400/60',
            shadow:
              'shadow-none dark:shadow-[0_0_35px_rgba(52,211,153,0.3)]',
          },
          {
            ring: 'ring-0 dark:ring-2 dark:ring-rose-400/60',
            shadow:
              'shadow-none dark:shadow-[0_0_35px_rgba(251,113,133,0.3)]',
          },
          {
            ring: 'ring-0 dark:ring-2 dark:ring-amber-400/60',
            shadow:
              'shadow-none dark:shadow-[0_0_35px_rgba(251,191,36,0.3)]',
          },
          {
            ring: 'ring-0 dark:ring-2 dark:ring-cyan-400/60',
            shadow:
              'shadow-none dark:shadow-[0_0_35px_rgba(103,232,249,0.3)]',
          },
        ];
        const glow = glowVariants[idx % glowVariants.length];
        return (
          <div
            key={product.id || idx}
            className={`relative overflow-hidden min-h-screen ${idx % 2 === 0 ? 'bg-slate-50 dark:bg-slate-900' : 'bg-slate-100 dark:bg-slate-800'} flex items-center justify-center px-6 py-16 transition-colors duration-300`}
            dir={isArabic || isPersian ? 'rtl' : 'ltr'}
          >
            {/* Aurora background (inspired by ReactBits Aurora) */}
            <div aria-hidden className="pointer-events-none absolute inset-0 -z-0">
              <div
                className="absolute -inset-[20%] blur-3xl opacity-30 dark:opacity-50 animate-[spin_80s_linear_infinite]"
                style={{
                  backgroundImage:
                    'radial-gradient(60% 60% at 15% 25%, rgba(99,102,241,0.35), transparent 60%), radial-gradient(55% 55% at 85% 30%, rgba(139,92,246,0.28), transparent 60%), radial-gradient(50% 60% at 50% 80%, rgba(16,185,129,0.28), transparent 60%)',
                }}
              />
              <div
                className="absolute -inset-[25%] opacity-20 dark:opacity-30 animate-[spin_120s_linear_reverse_infinite]"
                style={{
                  backgroundImage:
                    'radial-gradient(40% 40% at 70% 70%, rgba(34,211,238,0.25), transparent 60%), radial-gradient(45% 45% at 30% 70%, rgba(59,130,246,0.22), transparent 60%)',
                  WebkitMaskImage: 'radial-gradient(ellipse at center, black 35%, transparent 70%)',
                  maskImage: 'radial-gradient(ellipse at center, black 35%, transparent 70%)',
                }}
              />
            </div>

            <div className="relative z-10 max-w-7xl w-full">
              {/* Header Section */}
              <div className="text-center mb-16">
                <h3 className="gs-reveal opacity-0 translate-y-6 text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-100 mb-4 leading-tight transition-transform duration-700">{(isArabic || isPersian) ? t('gifSection.title') || title : title}</h3>

                <p className="gs-reveal opacity-0 translate-y-6 text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto leading-relaxed mb-10 transition-transform duration-700" data-delay="120">{(isArabic || isPersian) ? t('gifSection.description') || description : description}</p>

                <button
                  className="gs-reveal opacity-0 translate-y-6 group inline-flex cursor-pointer items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 border border-slate-300 text-slate-900 bg-white hover:bg-slate-100 hover:border-slate-400 hover:shadow-md hover:-translate-y-0.5 dark:border-slate-600 dark:text-slate-100 dark:bg-slate-700 dark:hover:bg-slate-600 dark:hover:border-slate-500 dark:hover:shadow-md focus:outline-none focus:ring-2 focus:ring-slate-400/50 dark:focus:ring-slate-300/30"
                  data-delay="240"
                  onClick={() => (window.location.href = `/detailed-product?product=${product.id}`)}
                >
                  {t('gifSection.learnMore') || 'Learn more'}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
                </button>
              </div>

              {/* Visual Section */}
              <div className="relative flex items-center justify-center gap-16">
                {/* Left side - Dashboard Video */}
                <div className="relative flex justify-end z-10">
                  <div className="relative transition-transform duration-500">
                    <div className={`gs-reveal opacity-0 -translate-x-6 scale-95 w-full max-w-xl rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 ${glow.ring} ${ringOffset} ${glow.shadow} transition-all duration-700 hover:-translate-y-1 hover:scale-[1.02]`} data-delay="120">
                      <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-full h-auto"
                        poster={product.slogan_loading_image || product.slogan_image}
                      >
                        <source src={product.slogan_video} type="video/mp4" />
                        <img
                          src={product.slogan_image}
                          alt="Dashboard interface"
                          className="w-full h-auto"
                        />
                      </video>
                    </div>
                  </div>
                </div>

                {/* Right side - Product Image (hidden on small screens) */}
                <div className="hidden md:flex w-1/4 justify-start pl-0 -ml-20 z-0">
                  <div className="relative transform  transition-transform duration-500">
                    <div className="gs-reveal opacity-0 translate-x-6 scale-95 w-full max-w-md rounded-2xl shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 transition-all duration-700" data-delay="220">
                      {/* Professional workspace video/image */}
                      <img
                        src={rightImage}
                        alt={product.name || 'Product image'}
                        className="w-full h-96 object-cover"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}

export default VideoSection;




